import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import {
  CreateShopHeader,
  CreateShopInfoStep,
  CreateShopMobileShell,
  CreateShopPayoutStep,
  CreateShopPreviewSidebar,
  CreateShopReviewStep,
  CreateShopShippingStep,
  CreateShopSocialsStep,
  CreateShopStepper,
  CreateShopTeamStep,
} from "@/components/Organisms";
import { CreateShopWizardNav } from "@/components/Molecules";
import type { CreateShopTemplateProps } from "./CreateShop.template.types";
import * as styles from "./CreateShop.template.styles";

export type { CreateShopTemplateProps } from "./CreateShop.template.types";

export function CreateShopTemplate(props: CreateShopTemplateProps) {
  const { t } = useTranslation("shops");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const mobileProgressPercent = Number.parseFloat(props.mobileProgressWidth);

  const navigationProps = {
    isLastStep: props.isLastStep,
    showBack: props.showBack,
    nextDisabled: props.nextDisabled,
    isSubmitting: props.isSubmitting,
    onNext: props.goNext,
    onBack: props.goBack,
    onFinish: () => void props.handleFinish(),
  };

  const stepContent = {
    info: <CreateShopInfoStep {...props} />,
    socials: <CreateShopSocialsStep {...props} />,
    shipping: <CreateShopShippingStep {...props} />,
    team: <CreateShopTeamStep {...props} />,
    payout: <CreateShopPayoutStep {...props} />,
    review: <CreateShopReviewStep {...props} />,
  }[props.step];

  if (isDesktop) {
    return (
      <Box sx={styles.root}>
        <CreateShopHeader />
        <Box component="main" sx={styles.main}>
          <Box sx={styles.grid}>
            <Box sx={styles.formColumn}>
              <Box sx={styles.pageHeading}>
                <Typography sx={styles.pageTitle}>{t("title")}</Typography>
                <Typography sx={styles.pageSubtitle}>{t("subtitle")}</Typography>
              </Box>
              <CreateShopStepper {...props} />
              <Box sx={styles.wizardCard}>
                {stepContent}
                <Box sx={styles.navigation}>
                  <CreateShopWizardNav {...navigationProps} />
                </Box>
              </Box>
            </Box>
            <CreateShopPreviewSidebar {...props} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <CreateShopMobileShell
        {...navigationProps}
        stepIndex={props.stepIndex}
        stepName={t(`steps.${props.step}`)}
        progressPercent={mobileProgressPercent}
      >
        {stepContent}
      </CreateShopMobileShell>
    </Box>
  );
}
